import { TokenWithBalance } from '@/domain/common/types'
import { Percentage } from '@/domain/types/NumericValues'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { BridgeToken } from '@/features/bridge/logic/useBridge'
import { triggerWalletConnect } from '@/features/analytics'
import { ToggleButton } from '@/features/swap/components/forms/swap-form/ToggleButton'
import { Button } from '@/ui/atoms/button/Button'
import { Checkbox } from '@/ui/atoms/checkbox/Checkbox'
import { Form, FormControl, FormItem, FormMessage } from '@/ui/atoms/form/Form'
import { Input } from '@/ui/atoms/input/Input'
import { Label } from '@/ui/atoms/label/Label'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Tooltip } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipContentShort } from '@/ui/atoms/tooltip/Tooltip'
import { Typography } from '@/ui/atoms/typography/Typography'
import { InfoIcon } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import TradeButton from '../../TradeButton'
import { TradeCurrentPage, UseTradeResults, calculateToAmount } from '../../logic/useTrade'
import { From } from './From'
import { To } from './To'

type TabType = 'Market' | 'Limit' | 'TWAP'

// Sort assets: EVM with balance, Core with balance, EVM without balance, Core without balance
function sortAssetsByNetworkAndBalance(assets: (BridgeToken | TokenWithBalance)[]) {
  return [...assets].sort((a, b) => {
    const aIsEvm = !('tokenId' in a)
    const bIsEvm = !('tokenId' in b)
    const aHasBalance = a.balance.gt(0)
    const bHasBalance = b.balance.gt(0)

    // Assets with balance come before assets without balance
    if (aHasBalance !== bHasBalance) {
      return aHasBalance ? -1 : 1
    }

    // Within same balance group, EVM comes before Core
    if (aIsEvm !== bIsEvm) {
      return aIsEvm ? -1 : 1
    }

    return 0
  })
}

export function TradeForm(
  props: UseTradeResults & {
    isL1Trade?: boolean
    isEvmTrade?: boolean
    isTransferAfter: boolean
    setErrorMessage: (s: string) => void
    slippage: Percentage
    activeTab: TabType
    onLimitPriceChange?: (price: string) => void
    currentPage: TradeCurrentPage
    lockToToken?: boolean
  },
) {
  const {
    form,
    guestMode,
    fromTokenField,
    toTokenField,
    openConnectModal,
    actions,
    pageStatus,
    swapDirection,
    setErrorMessage,
    exchangeRate,
    slippage,
    swapQuoteData,
    isEvmTrade,
    isL1Trade,
    isTransferAfter,
    isExchangeRateLoading,
    activeTab,
    onLimitPriceChange,
    currentPage,
    lockToToken,
    // Gas limit modal state
    isUsingBigBlocks,
    handleGasLimitExceeded,
    isBothDepositTokens,
  } = props

  const sortedFromAssets = useMemo(() => {
    const sorted = sortAssetsByNetworkAndBalance(fromTokenField.availableAssets)
    if (lockToToken) {
      const lockedSymbol = toTokenField.selectedAsset.token.symbol
      return sorted.filter((asset) => asset.token.symbol !== lockedSymbol)
    }
    return sorted
  }, [fromTokenField.availableAssets, lockToToken, toTokenField.selectedAsset.token.symbol])

  const sortedToAssets = useMemo(
    () => sortAssetsByNetworkAndBalance(toTokenField.availableAssets),
    [toTokenField.availableAssets],
  )

  const toAssetsRef = useRef(toTokenField.availableAssets)
  toAssetsRef.current = toTokenField.availableAssets

  const limitPrice = form.watch('limitPrice') || ''
  const previousActiveTab = useRef<TabType>('Market')
  const previousExchangeRateLoading = useRef(isExchangeRateLoading)

  // Watch for token changes to update limit price
  const fromSymbol = form.watch('from.symbol')
  const toSymbol = form.watch('to.symbol')
  const fromIsL1 = form.watch('from.isL1')
  const toIsL1 = form.watch('to.isL1')
  const previousTokenPair = useRef<string>('')
  const isRateLimited = isEvmTrade && !!swapQuoteData && 'error' in swapQuoteData && swapQuoteData.statusCode === 429
  const rateLimitedMessage =
    isEvmTrade && swapQuoteData && 'error' in swapQuoteData && swapQuoteData.statusCode === 429
      ? swapQuoteData.error
      : null

  // Set limit price as default when switching to limit mode (only once)
  useEffect(() => {
    if (
      activeTab === 'Limit' &&
      previousActiveTab.current !== 'Limit' &&
      exchangeRate &&
      exchangeRate !== 'N/A' &&
      !limitPrice
    ) {
      form.setValue('limitPrice', exchangeRate, { shouldValidate: false })
    }
    previousActiveTab.current = activeTab
  }, [activeTab, form, exchangeRate, limitPrice])

  // Set limit price when exchange rate loading completes while in limit mode
  useEffect(() => {
    if (
      activeTab === 'Limit' &&
      previousExchangeRateLoading.current &&
      !isExchangeRateLoading &&
      exchangeRate &&
      exchangeRate !== 'N/A' &&
      !limitPrice
    ) {
      form.setValue('limitPrice', exchangeRate, { shouldValidate: false })
    }
    previousExchangeRateLoading.current = isExchangeRateLoading
  }, [activeTab, isExchangeRateLoading, exchangeRate, limitPrice, form])

  // Update limit price when token pair changes in limit mode
  useEffect(() => {
    if (activeTab === 'Limit') {
      const currentTokenPair = `${fromSymbol}-${toSymbol}-${fromIsL1}-${toIsL1}`
      if (previousTokenPair.current && previousTokenPair.current !== currentTokenPair) {
        // Token pair changed, update limit price with current exchange rate
        if (exchangeRate && exchangeRate !== 'N/A') {
          form.setValue('limitPrice', exchangeRate, { shouldValidate: false })
        }
      }
      previousTokenPair.current = currentTokenPair
    }
  }, [activeTab, fromSymbol, toSymbol, fromIsL1, toIsL1, exchangeRate, form])

  // Notify parent component about limit price changes
  useEffect(() => {
    if (onLimitPriceChange) {
      onLimitPriceChange(limitPrice)
    }
  }, [limitPrice, onLimitPriceChange])

  const handleLimitPriceChange = (price: string) => {
    form.setValue('limitPrice', price, { shouldValidate: false })

    // Recalculate "to" value when limit price changes in limit mode
    if (activeTab === 'Limit' && price && Number(price) > 0) {
      const fromValue = form.getValues('from.value')
      if (fromValue && Number(fromValue) > 0) {
        const currentFormValues = {
          from: { symbol: form.getValues('from.symbol'), isL1: form.getValues('from.isL1') },
          to: { symbol: form.getValues('to.symbol'), isL1: form.getValues('to.isL1') },
        }
        calculateToAmount(form, price, toAssetsRef, NormalizedUnitNumber(fromValue), currentFormValues)
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(() => {})}>
        <input type="hidden" name="tradeMode" />
        <div className="relative z-10">
          <From
            control={form.control}
            assets={sortedFromAssets}
            selectedAsset={fromTokenField.selectedAsset}
            changeToken={fromTokenField.changeToken}
            maxSelectedFieldName={fromTokenField.maxSelectedFieldName}
            onChange={fromTokenField.onChange}
            onSetMax={fromTokenField.onSetMax}
            maxValue={fromTokenField.maxValue}
          />

          {activeTab === 'Limit' && (
            <Panel.Wrapper className="mt-2 p-4 pb-5">
              <FormItem>
                <div className="flex flex-row items-center gap-1">
                  <Typography variant="h4" className="py-3 font-semibold">
                    Price
                  </Typography>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      type="number"
                      step="any"
                      placeholder="0"
                      value={limitPrice}
                      min={0}
                      onChange={(e) => handleLimitPriceChange(e.target.value)}
                      className="border-input bg-input pr-16 text-foreground placeholder:text-muted-foreground "
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (exchangeRate && exchangeRate !== 'N/A') {
                          handleLimitPriceChange(exchangeRate)
                        }
                      }}
                      className="-translate-y-1/2 absolute top-1/2 right-2 rounded bg-primary/10 px-2 py-1 font-medium text-primary text-xs transition-colors hover:bg-primary/20 disabled:opacity-50"
                      disabled={!exchangeRate || exchangeRate === 'N/A'}
                    >
                      Mid
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            </Panel.Wrapper>
          )}

          {!lockToToken && (
            <ToggleButton
              className="absolute right-1/2 bottom-0 translate-x-1/2 translate-y-3/4"
              onClick={swapDirection}
            />
          )}
        </div>
        <To
          control={form.control}
          assets={sortedToAssets}
          selectedAsset={toTokenField.selectedAsset}
          changeToken={toTokenField.changeToken}
          maxSelectedFieldName={toTokenField.maxSelectedFieldName}
          onChange={toTokenField.onChange}
          lockToToken={lockToToken}
        />

        {activeTab === 'TWAP' && (
          <Panel.Wrapper className="mt-2 p-4 pb-5">
            <FormItem>
              <div className="flex flex-row items-center gap-1">
                <Typography variant="h4" className="py-3 font-semibold">
                  Running Time (5m - 24h)
                </Typography>
              </div>
              <FormControl>
                <div className="flex flex-row items-center gap-2">
                  <div className="relative w-full">
                    <Input
                      type="number"
                      placeholder="0"
                      value={form.watch('twapHours') || ''}
                      min={0}
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value) || 0
                        form.setValue('twapHours', Math.max(0, value), { shouldValidate: true })
                        // Trigger validation manually
                        form.trigger(['twapHours', 'from', 'to'])
                      }}
                      className="border-input bg-input pr-14 text-foreground placeholder:text-muted-foreground"
                    />
                    <Typography
                      variant="p"
                      className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 text-secondary-foreground"
                    >
                      hours
                    </Typography>
                  </div>
                  <div className="relative w-full">
                    <Input
                      type="number"
                      placeholder="0"
                      value={form.watch('twapMinutes') || ''}
                      min={0}
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value) || 0
                        form.setValue('twapMinutes', Math.max(0, value), { shouldValidate: true })
                        // Trigger validation manually
                        form.trigger(['twapHours', 'from', 'to'])
                      }}
                      className={`border-input bg-input pr-16 text-foreground placeholder:text-muted-foreground ${
                        form.formState.errors.twapHours?.message && 'mb-1 border-red-400 bg-red-400/10'
                      }`}
                    />
                    <Typography
                      variant="p"
                      className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 text-secondary-foreground"
                    >
                      minutes
                    </Typography>
                  </div>
                </div>
              </FormControl>
              <div>
                {form.formState.errors.twapHours?.message && (
                  <Typography variant="prompt" className="text-red-400">
                    {form.formState.errors.twapHours?.message}
                  </Typography>
                )}
              </div>
            </FormItem>

            <FormItem className="mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="twapRandomize"
                  checked={form.watch('twapRandomize') || false}
                  onCheckedChange={(checked) =>
                    form.setValue('twapRandomize', checked as boolean, { shouldValidate: false })
                  }
                />
                <div className="flex items-center gap-1">
                  <Label htmlFor="twapRandomize" className="text-foreground text-sm">
                    Randomize
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoIcon className="h-4 w-4 cursor-help text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContentShort>
                      If Randomize is enabled, the size of each sub-trade will be automatically adjusted within a
                      certain range, typically up to 20% higher or lower than the original trade size. However, any
                      other requirements specified when creating the TWAP trade, such as ensuring the randomized trade
                      size does not exceed the maximum single trade size, will still be followed.
                    </TooltipContentShort>
                  </Tooltip>
                </div>
              </div>
            </FormItem>
          </Panel.Wrapper>
        )}

        {isBothDepositTokens && (
          <Panel.Wrapper className="mt-2 gap-2 border-yellow-500/20 bg-yellow-500/10 p-4 md:p-4">
            <Panel.Content>
              <Typography className="text-sm text-yellow-500">
                Direct swaps between deposit tokens (hyTokens, eTokens) have limited support and are likely to fail. Try
                swapping to the underlying asset first.
              </Typography>
            </Panel.Content>
          </Panel.Wrapper>
        )}

        {((isEvmTrade && swapQuoteData && 'error' in swapQuoteData) ||
          (isTransferAfter && !(isEvmTrade || isL1Trade)) ||
          (exchangeRate === 'N/A' && !isExchangeRateLoading)) && (
          <Panel className="mt-2 gap-2 p-4 md:p-4">
            <Panel.Header className="font-semibold text-foreground">
              {isRateLimited ? 'Quote temporarily unavailable' : 'No routes available'}
            </Panel.Header>
            <Panel.Content>
              <Typography className="text-muted-foreground text-sm">
                {isRateLimited
                  ? rateLimitedMessage
                  : 'Reasons for that could be: low liquidity, amount selected is too low, gas costs are too high or there are no routes for the selected combination.'}
              </Typography>
            </Panel.Content>
          </Panel>
        )}

        <div className="mt-4">
          <div className="mb-2 text-muted-foreground text-sm" />
          {guestMode ? (
            <Button className="w-full" onClick={() => void triggerWalletConnect(openConnectModal, 'trade_form')}>
              Connect wallet
            </Button>
          ) : (
            <TradeButton
              objectives={actions}
              swapQuoteData={swapQuoteData}
              onFinish={pageStatus.goToSuccessScreen}
              enabled={pageStatus.actionsEnabled}
              setErrorMessage={setErrorMessage}
              exchangeRate={exchangeRate}
              slippage={slippage}
              fromAssets={fromTokenField.availableAssets}
              toAssets={toTokenField.availableAssets}
              tradingWithAssetsSymbols={props.tradingWithAssetsSymbols}
              activeTab={activeTab}
              limitPrice={limitPrice}
              currentPage={currentPage}
              isUsingBigBlocks={isUsingBigBlocks}
              onGasLimitExceeded={handleGasLimitExceeded}
              twapParams={
                activeTab === 'TWAP'
                  ? {
                      hours: form.watch('twapHours') || 0,
                      minutes: form.watch('twapMinutes') || 0,
                      randomize: form.watch('twapRandomize') || false,
                    }
                  : undefined
              }
            />
          )}
        </div>
      </form>
    </Form>
  )
}
